/**
 * `/$org/apps`: one page for every app. The apps the organization uses are
 * large tiles at the top, each opening its own area or page. Below them,
 * every other app it can add, as smaller tiles with category chips and a
 * search. No sidebar: the page is the whole card.
 *
 * @module components/apps/AppsPage
 */
import { SearchIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "../../lib/utils";
import { Bone } from "../regulation/LoadingSkeletons";
import { Input } from "../ui/input";
import { AppIcon, AppLink, useAppEntries } from "./AppParts";
import {
  APP_CATEGORIES,
  filterAppEntries,
  groupAppEntries,
  type AppCategory,
  type AppEntry,
} from "./appRegistry";
import { launcherSections, launcherTileCaption } from "./appsLauncher";
import { ConnectMcpButton } from "./integrations/ConnectMcpButton";

const TILE_CLASS =
  "group flex min-w-0 rounded-2xl border border-border/70 bg-card outline-none transition-[border-color,background-color,transform] duration-150 hover:border-foreground/20 hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]";

function InUseTile({ entry, orgSegment }: { readonly entry: AppEntry; readonly orgSegment: string }) {
  const connected = entry.kind !== "first-party" && entry.availability === "connected";
  return (
    <AppLink
      entry={entry}
      orgSegment={orgSegment}
      className={cn(TILE_CLASS, "min-h-32 flex-col justify-between gap-6 p-4")}
      data-apps-tile={entry.id}
    >
      <AppIcon entry={entry} className="size-11 rounded-xl" />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-foreground">{entry.name}</span>
        <span className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
          {connected ? <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-success" /> : null}
          {launcherTileCaption(entry)}
        </span>
      </span>
    </AppLink>
  );
}

function AvailableTile({ entry, orgSegment }: { readonly entry: AppEntry; readonly orgSegment: string }) {
  const soon = entry.kind === "coming-soon";
  return (
    <AppLink
      entry={entry}
      orgSegment={orgSegment}
      className={cn(TILE_CLASS, "items-center gap-3 p-3", soon && "opacity-70")}
      data-apps-available={entry.id}
    >
      <AppIcon entry={entry} className="size-9 rounded-lg" />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-[13px] font-medium text-foreground">{entry.name}</span>
          {soon ? (
            <span className="shrink-0 rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground">
              Soon
            </span>
          ) : null}
        </span>
        <span className="line-clamp-2 text-xs text-muted-foreground">{entry.description}</span>
      </span>
    </AppLink>
  );
}

export function AppsPage({
  organizationId,
  orgSegment,
  orgName,
  category,
  onCategoryChange,
}: {
  readonly organizationId: string | null;
  readonly orgSegment: string;
  readonly orgName: string | null;
  readonly category: AppCategory | null;
  readonly onCategoryChange: (category: AppCategory | null) => void;
}) {
  const [search, setSearch] = useState("");
  const { entries, isPending } = useAppEntries(organizationId);
  const { inUse, available } = useMemo(() => launcherSections(entries), [entries]);
  const shown = useMemo(
    () => groupAppEntries(filterAppEntries(available, { category, query: search })),
    [available, category, search],
  );
  const chips = APP_CATEGORIES.filter((entry) => available.some((app) => app.category === entry.id));
  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-apps-page="">
      <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-8 sm:py-10">
        <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <h1 className="text-[22px] font-semibold tracking-[-0.01em] text-foreground">Apps</h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {orgName ? `${orgName} · ` : ""}
              {isPending ? "Loading…" : `${inUse.length} in use`}
            </p>
          </div>
          <ConnectMcpButton organizationId={organizationId} orgSegment={orgSegment} />
        </header>

        <section aria-label="Apps in use" className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {isPending
            ? Array.from({ length: 4 }, (_, index) => <Bone key={index} className="h-32 rounded-2xl" />)
            : inUse.map((entry) => (
                <InUseTile key={`${entry.kind}:${entry.id}`} entry={entry} orgSegment={orgSegment} />
              ))}
        </section>

        {!isPending && available.length > 0 ? (
          <section aria-labelledby="apps-available" className="mt-12">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 id="apps-available" className="text-[15px] font-semibold text-foreground">
                Add to {orgName ?? "your organization"}
              </h2>
              <label className="relative block w-full max-w-60">
                <span className="sr-only">Search apps</span>
                <SearchIcon
                  aria-hidden
                  className="pointer-events-none absolute left-2.5 top-1/2 z-10 size-3.5 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search apps"
                  className="pl-7"
                  size="sm"
                />
              </label>
            </div>
            <div role="group" aria-label="Category" className="mt-3 flex flex-wrap gap-1.5">
              {[{ id: null, label: "All" } as const, ...chips].map((chip) => {
                const active = category === chip.id;
                return (
                  <button
                    key={chip.id ?? "all"}
                    type="button"
                    aria-pressed={active}
                    data-apps-chip={chip.id ?? "all"}
                    onClick={() => onCategoryChange(chip.id)}
                    className={cn(
                      "h-7 cursor-pointer rounded-full border px-3 text-xs transition-colors",
                      active
                        ? "border-foreground bg-foreground text-background"
                        : "border-border text-muted-foreground hover:border-foreground/25 hover:text-foreground",
                    )}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
            {shown.length === 0 ? (
              <p className="mt-8 text-center text-[13px] text-muted-foreground">No apps match.</p>
            ) : (
              shown.map((group) => (
                <div key={group.category} className="mt-6">
                  <h3 className="mb-2 text-xs font-medium text-muted-foreground">{group.label}</h3>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {group.entries.map((entry) => (
                      <AvailableTile key={`${entry.kind}:${entry.id}`} entry={entry} orgSegment={orgSegment} />
                    ))}
                  </div>
                </div>
              ))
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}
